// src/pages/JobEnquiry.js

import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles/EnquiryForm.scss';

const JobEnquiry = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        position: '',
        experience: '',
        qualification: '',
        currentEmployer: '',
        expectedSalary: '',
        coverLetter: '',
        resume: null
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const handleChange = (e) => {
        if (e.target.name === 'resume') {
            const file = e.target.files[0];
            if (file) {
                // Validate file type
                const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
                if (!allowedTypes.includes(file.type)) {
                    setMessage({ type: 'error', text: 'Only PDF, DOC, and DOCX files are allowed' });
                    return;
                }
                // Validate file size (5MB = 5 * 1024 * 1024 bytes)
                if (file.size > 5 * 1024 * 1024) {
                    setMessage({ type: 'error', text: 'File size must be less than 5MB' });
                    return;
                }
                setMessage({ type: '', text: '' });
            }
            setFormData({
                ...formData,
                [e.target.name]: file
            });
        } else {
            setFormData({
                ...formData,
                [e.target.name]: e.target.value
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const formDataToSend = new FormData();
            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== '') {
                    formDataToSend.append(key, formData[key]);
                }
            });

            const apiUrl = process.env.REACT_APP_API_URL || 'https://vhserver.desync.in';
            const response = await fetch(`${apiUrl}/public/api/school/ptvn_goraul/job-enquiry`, {
                method: 'POST',
                body: formDataToSend
            });

            const data = await response.json();

            if (data.success) {
                setMessage({ type: 'success', text: 'Job application submitted successfully! We will review your application and contact you soon.' });
                setFormData({
                    fullName: '',
                    email: '',
                    phoneNumber: '',
                    position: '',
                    experience: '',
                    qualification: '',
                    currentEmployer: '',
                    expectedSalary: '',
                    coverLetter: '',
                    resume: null
                });
                // Reset file input
                const fileInput = document.getElementById('resume');
                if (fileInput) fileInput.value = '';
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to submit application. Please try again.' });
            }
        } catch (error) {
            console.error('Error submitting job application:', error);
            setMessage({ type: 'error', text: 'Failed to submit application. Please check your connection and try again.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="enquiry-form-container">
            <Helmet>
                <title>Job Application - Prabhat Tara Vidya Niketan</title>
                <meta name="description" content="Apply for teaching and non-teaching positions at Prabhat Tara Vidya Niketan. Submit your job application online." />
                <meta name="keywords" content="jobs, career, teaching jobs, Prabhat Tara Vidya Niketan, job application" />
            </Helmet>

            <div className="enquiry-form">
                <div className="form-header">
                    <h1>Job Application</h1>
                    <p>Join Our Teaching Community</p>
                </div>

                {message.text && (
                    <div className={`message ${message.type}`}>
                        {message.text}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="enquiry-form-content" encType="multipart/form-data">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="fullName">Full Name *</label>
                            <input
                                type="text"
                                id="fullName"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                required
                                placeholder="Enter your full name"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email Address *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                placeholder="Enter your email"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="phoneNumber">Phone Number *</label>
                            <input
                                type="tel"
                                id="phoneNumber"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                required
                                placeholder="Enter your phone number"
                                pattern="[0-9]{10}"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="position">Position Applied For *</label>
                            <select
                                id="position"
                                name="position"
                                value={formData.position}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Position</option>
                                <option value="Primary Teacher">Primary Teacher</option>
                                <option value="Secondary Teacher">Secondary Teacher</option>
                                <option value="English Teacher">English Teacher</option>
                                <option value="Hindi Teacher">Hindi Teacher</option>
                                <option value="Mathematics Teacher">Mathematics Teacher</option>
                                <option value="Science Teacher">Science Teacher</option>
                                <option value="Social Science Teacher">Social Science Teacher</option>
                                <option value="Computer Teacher">Computer Teacher</option>
                                <option value="Physical Education Teacher">Physical Education Teacher</option>
                                <option value="Art Teacher">Art Teacher</option>
                                <option value="Music Teacher">Music Teacher</option>
                                <option value="Librarian">Librarian</option>
                                <option value="Administrative Staff">Administrative Staff</option>
                                <option value="Support Staff">Support Staff</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="experience">Experience *</label>
                            <select
                                id="experience"
                                name="experience"
                                value={formData.experience}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Experience</option>
                                <option value="Fresher">Fresher</option>
                                <option value="1-2 years">1-2 years</option>
                                <option value="3-5 years">3-5 years</option>
                                <option value="6-10 years">6-10 years</option>
                                <option value="10+ years">10+ years</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="qualification">Highest Qualification *</label>
                            <input
                                type="text"
                                id="qualification"
                                name="qualification"
                                value={formData.qualification}
                                onChange={handleChange}
                                required
                                placeholder="e.g., B.Ed, M.A, B.Sc etc."
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="currentEmployer">Current Employer</label>
                            <input
                                type="text"
                                id="currentEmployer"
                                name="currentEmployer"
                                value={formData.currentEmployer}
                                onChange={handleChange}
                                placeholder="Current school/organization (if any)"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="expectedSalary">Expected Salary</label>
                            <input
                                type="text"
                                id="expectedSalary"
                                name="expectedSalary"
                                value={formData.expectedSalary}
                                onChange={handleChange}
                                placeholder="Expected salary range"
                            />
                        </div>
                    </div>

                    <div className="form-group full-width">
                        <label htmlFor="resume">Resume/CV *</label>
                        <input
                            type="file"
                            id="resume"
                            name="resume"
                            onChange={handleChange}
                            required
                            accept=".pdf,.doc,.docx"
                            className="file-input"
                        />
                        <small>Upload PDF, DOC, or DOCX file (Max size: 5MB)</small>
                    </div>

                    <div className="form-group full-width">
                        <label htmlFor="coverLetter">Cover Letter/Additional Information</label>
                        <textarea
                            id="coverLetter"
                            name="coverLetter"
                            value={formData.coverLetter}
                            onChange={handleChange}
                            placeholder="Tell us why you want to join our school and what makes you a good fit for this position"
                            rows="5"
                        />
                    </div>

                    <button type="submit" className="submit-btn" disabled={loading}>
                        {loading ? 'Submitting...' : 'Submit Application'}
                    </button>
                </form>

                <div className="contact-info">
                    <h3>Contact Information</h3>
                    <p><strong>School:</strong> Prabhat Tara Vidya Niketan</p>
                    <p><strong>Location:</strong> Goraul, Vaishali, Bihar</p>
                    <p><strong>Phone:</strong> +91 7654164726</p>
                    <p><strong>Note:</strong> Our HR team will review your application and contact qualified candidates within 5-7 working days.</p>
                </div>
            </div>
        </div>
    );
};

export default JobEnquiry;
